import { Component, effect, input } from '@angular/core';
import { CamelCasePipe } from '@shared/pipes';
import {
  BadgeStyle,
  RiskLevelColour,
  RiskScoreStandardized
} from '@shared/types';
import { BadgeComponent } from '../../../badge.component';

// eslint-disable-next-line @nx/enforce-module-boundaries

@Component({
  selector: 'dxc-risk-indicator-bar',
  imports: [BadgeComponent],
  templateUrl: './risk-indicator-bar.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `
  ]
})
export class RiskIndicatorBarComponent {
  risk = input.required<
    RiskScoreStandardized & { dxcCalcScore?: number | null }
  >();
  riskDetail: { level: string; badgeStyle: BadgeStyle; barColour: string } = {
    level: 'Low',
    badgeStyle: 'white',
    barColour: ''
  };

  secondaryRiskDetail: {
    level: string;
    badgeStyle: BadgeStyle;
    barColour: string;
  } | null = null;

  constructor() {
    effect(() => {
      this.updateRiskDetails();
    });
  }

  private updateRiskDetails(): void {
    const primaryScore = this.risk().score;
    const secondaryScore = this.risk().dxcCalcScore;

    if (primaryScore !== null && primaryScore !== undefined) {
      this.riskDetail = this.getRiskDetail(primaryScore);
    }

    if (secondaryScore !== null && secondaryScore !== undefined) {
      this.secondaryRiskDetail = this.getRiskDetail(secondaryScore);
      this.secondaryRiskDetail.badgeStyle = 'white';
    } else {
      this.secondaryRiskDetail = null;
    }
  }

  private getRiskDetail(score: number): {
    level: string;
    badgeStyle: BadgeStyle;
    barColour: string;
  } {
    if (score < 3) {
      return {
        level: 'Low',
        badgeStyle: 'green-outline',
        barColour: RiskLevelColour.low
      };
    } else if (score < 4) {
      return {
        level: 'Medium',
        badgeStyle: 'yellow-outline',
        barColour: RiskLevelColour.medium
      };
    } else {
      return {
        level: 'High',
        badgeStyle: 'red-outline',
        barColour: RiskLevelColour.high
      };
    }
  }

  protected get tooltip(): string {
    const camelCasePipe = new CamelCasePipe();
    let tooltip =
      this.risk().type.toLowerCase() === 'pep'
        ? camelCasePipe.transform(this.risk().type, true)
        : camelCasePipe.transform(this.risk().type);

    tooltip += `: ${this.riskDetail.level} risk`;

    if (this.secondaryRiskDetail) {
      tooltip += ` (DXC Calc: ${this.risk().dxcCalcScore} - ${this.secondaryRiskDetail.level} risk)`;
    }

    return tooltip;
  }

  protected get primaryMarginStart(): string {
    const score = this.risk().score;
    if (score === null) {
      return '0px';
    }
    const percentage = ((score - 0) / 5) * 100;
    return `calc(${percentage}% - 1.25rem)`;
  }

  protected get secondaryMarginStart(): string {
    const score = this.risk().dxcCalcScore;
    if (score === null || score === undefined) {
      return '0px';
    }
    const percentage = ((score - 0) / 5) * 100;
    return `calc(${percentage}% - 1.25rem)`;
  }

  protected get barWidth(): string {
    if (this.secondaryRiskDetail) {
      const secondaryScore = this.risk().dxcCalcScore ?? 1;
      return `${(secondaryScore / 5) * 100}%`;
    } else {
      const primaryScore = this.risk().score ?? 1;
      return `${(primaryScore / 5) * 100}%`;
    }
  }

  protected get primaryBarWidth(): string {
    const primaryScore = this.risk().score ?? 0;
    return `${(primaryScore / 5) * 100}%`;
  }

  protected get secondaryBarWidth(): string {
    if (!this.secondaryRiskDetail) return '0%';
    const primaryScore = this.risk().score ?? 0;
    const secondaryScore = this.risk().dxcCalcScore ?? 0;
    const difference = secondaryScore - primaryScore;
    return `${(difference / 5) * 100}%`;
  }

  protected get barColour(): string {
    return this.secondaryRiskDetail
      ? this.secondaryRiskDetail.barColour
      : this.riskDetail.barColour;
  }
}
